package com.speedsoftware.nibbler.ui.boards

import androidx.compose.ui.draw.clip
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.BoardDto
import com.speedsoftware.nibbler.models.IssueType
import com.speedsoftware.nibbler.models.SprintDto
import com.speedsoftware.nibbler.models.TaskPriority
import com.speedsoftware.nibbler.models.UserDto
import com.speedsoftware.nibbler.ui.components.AssigneeAvatarStack
import com.speedsoftware.nibbler.ui.components.IssueTypeBadge
import com.speedsoftware.nibbler.ui.components.PriorityBadge
import com.speedsoftware.nibbler.ui.components.PropertyIcon
import com.speedsoftware.nibbler.ui.components.PropertyIconView
import com.speedsoftware.nibbler.ui.components.SprintStatusBadge
import com.speedsoftware.nibbler.ui.theme.NibblerColors

/** Board tabs as pills, with the board-level actions on the right. */
@Composable
fun BoardSwitcher(
    boards: List<BoardDto>,
    selectedIndex: Int,
    sprintCount: Int,
    onSelect: (Int) -> Unit,
    onAddBoard: () -> Unit,
    onEditBoard: () -> Unit,
    onSprints: () -> Unit
) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val compact = maxWidth < 760.dp
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .then(if (compact) Modifier.horizontalScroll(rememberScrollState()) else Modifier)
                .padding(horizontal = if (compact) 16.dp else 28.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            boards.forEachIndexed { index, board ->
                val selected = index == selectedIndex
                Row(
                    modifier = Modifier
                        .clip(RoundedCornerShape(20.dp))
                        .clickable { onSelect(index) }
                        .background(if (selected) NibblerColors.AccentSoft else Color.Transparent, RoundedCornerShape(20.dp))
                        .border(1.dp, if (selected) Color.Transparent else NibblerColors.OutlineVariant, RoundedCornerShape(20.dp))
                        .padding(horizontal = 14.dp, vertical = 7.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Text(
                        board.name,
                        fontSize = 13.sp,
                        fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
                        color = if (selected) NibblerColors.Accent else NibblerColors.OnSurface
                    )
                    Text("${board.tasks.size}", fontSize = 12.sp, color = if (selected) NibblerColors.Accent else NibblerColors.OnSurfaceVariant)
                }
            }
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(20.dp))
                    .clickable { onAddBoard() }
                    .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(20.dp))
                    .padding(horizontal = 10.dp, vertical = 8.dp)
            ) { PropertyIconView(PropertyIcon.PLUS, size = 14.dp) }

            if (!compact) Spacer(modifier = Modifier.weight(1f)) else Spacer(modifier = Modifier.width(8.dp))

            ToolbarButton(if (sprintCount > 0) "Sprints ($sprintCount)" else "Sprints", onSprints)
            ToolbarButton("Edit board", onEditBoard)
        }
    }
}

@Composable
private fun ToolbarButton(label: String, onClick: () -> Unit) {
    Text(
        label,
        fontSize = 13.sp,
        fontWeight = FontWeight.Medium,
        color = NibblerColors.OnSurface,
        modifier = Modifier
            .clip(RoundedCornerShape(10.dp))
            .clickable { onClick() }
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(10.dp))
            .padding(horizontal = 12.dp, vertical = 8.dp)
    )
}

/** Search box, filter pills, "only my issues", the people on the project and the board's completion. */
@Composable
fun QuickFilters(
    searchQuery: String,
    onSearch: (String) -> Unit,
    typeFilter: IssueType?,
    onType: (IssueType?) -> Unit,
    priorityFilter: TaskPriority?,
    onPriority: (TaskPriority?) -> Unit,
    sprintFilter: String?,
    sprints: List<SprintDto>,
    onSprint: (String?) -> Unit,
    onlyMine: Boolean,
    onOnlyMine: (Boolean) -> Unit,
    people: List<UserDto>,
    donePercent: Int?
) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val compact = maxWidth < 900.dp
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .then(if (compact) Modifier.horizontalScroll(rememberScrollState()) else Modifier)
                .padding(horizontal = if (compact) 16.dp else 28.dp)
                .padding(bottom = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            CompactSearchField(searchQuery, onSearch)

            var typeMenu by remember { mutableStateOf(false) }
            Box {
                FilterPill(typeFilter?.displayName ?: "Type", active = typeFilter != null, hasMenu = true) { typeMenu = true }
                DropdownMenu(typeMenu, { typeMenu = false }, modifier = Modifier.background(NibblerColors.SurfaceContainerHigh)) {
                    DropdownMenuItem(text = { Text("All types", color = NibblerColors.OnSurface, fontSize = 13.sp) }, onClick = { onType(null); typeMenu = false })
                    IssueType.values().forEach { t -> DropdownMenuItem(text = { IssueTypeBadge(t) }, onClick = { onType(t); typeMenu = false }) }
                }
            }

            var priorityMenu by remember { mutableStateOf(false) }
            Box {
                FilterPill(priorityFilter?.displayName ?: "Priority", active = priorityFilter != null, hasMenu = true) { priorityMenu = true }
                DropdownMenu(priorityMenu, { priorityMenu = false }, modifier = Modifier.background(NibblerColors.SurfaceContainerHigh)) {
                    DropdownMenuItem(text = { Text("All priorities", color = NibblerColors.OnSurface, fontSize = 13.sp) }, onClick = { onPriority(null); priorityMenu = false })
                    TaskPriority.values().forEach { p -> DropdownMenuItem(text = { PriorityBadge(p) }, onClick = { onPriority(p); priorityMenu = false }) }
                }
            }

            var sprintMenu by remember { mutableStateOf(false) }
            Box {
                val label = when (sprintFilter) {
                    null -> "Sprint"
                    "BACKLOG" -> "Backlog"
                    else -> sprints.firstOrNull { it.id == sprintFilter }?.name ?: "Sprint"
                }
                FilterPill(label, active = sprintFilter != null, hasMenu = true) { sprintMenu = true }
                DropdownMenu(sprintMenu, { sprintMenu = false }, modifier = Modifier.background(NibblerColors.SurfaceContainerHigh)) {
                    DropdownMenuItem(text = { Text("All issues", color = NibblerColors.OnSurface, fontSize = 13.sp) }, onClick = { onSprint(null); sprintMenu = false })
                    DropdownMenuItem(text = { Text("Backlog (no sprint)", color = NibblerColors.OnSurface, fontSize = 13.sp) }, onClick = { onSprint("BACKLOG"); sprintMenu = false })
                    sprints.forEach { sprint ->
                        DropdownMenuItem(
                            text = {
                                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    SprintStatusBadge(sprint.status)
                                    Text(sprint.name, color = NibblerColors.OnSurface, fontSize = 13.sp)
                                }
                            },
                            onClick = { onSprint(sprint.id); sprintMenu = false }
                        )
                    }
                }
            }

            FilterPill("Only my issues", active = onlyMine, hasCheck = onlyMine, hasMenu = false) { onOnlyMine(!onlyMine) }

            if (!compact) Spacer(modifier = Modifier.weight(1f)) else Spacer(modifier = Modifier.width(8.dp))

            if (people.isNotEmpty()) AssigneeAvatarStack(people, size = 26.dp, maxShown = 4)
            if (donePercent != null) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text("Done $donePercent%", fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant)
                    Box(modifier = Modifier.width(80.dp).height(6.dp).background(NibblerColors.SurfaceContainerHighest, RoundedCornerShape(3.dp))) {
                        if (donePercent > 0) {
                            Box(modifier = Modifier.fillMaxWidth(donePercent / 100f).height(6.dp).background(NibblerColors.Success, RoundedCornerShape(3.dp)))
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FilterPill(label: String, active: Boolean, hasMenu: Boolean, hasCheck: Boolean = false, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(20.dp))
            .clickable { onClick() }
            .background(if (active) NibblerColors.AccentSoft else Color.Transparent, RoundedCornerShape(20.dp))
            .border(1.dp, if (active) NibblerColors.Accent.copy(alpha = 0.5f) else NibblerColors.OutlineVariant, RoundedCornerShape(20.dp))
            .padding(horizontal = 14.dp, vertical = 7.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Text(label, fontSize = 13.sp, fontWeight = FontWeight.Medium, color = if (active) NibblerColors.Accent else NibblerColors.OnSurface)
        if (hasCheck) PropertyIconView(PropertyIcon.CHECK, size = 14.dp, color = NibblerColors.Accent)
        if (hasMenu) PropertyIconView(PropertyIcon.CHEVRON_DOWN, size = 14.dp, color = if (active) NibblerColors.Accent else NibblerColors.OnSurfaceVariant)
    }
}

/** A search field with an exact, compact height (Material text fields are at least 56dp tall). */
@Composable
private fun CompactSearchField(value: String, onChange: (String) -> Unit) {
    BasicTextField(
        value = value,
        onValueChange = onChange,
        singleLine = true,
        textStyle = TextStyle(fontSize = 13.sp, color = NibblerColors.OnSurface),
        cursorBrush = SolidColor(NibblerColors.Accent),
        modifier = Modifier.widthIn(min = 180.dp, max = 260.dp),
        decorationBox = { inner ->
            Row(
                modifier = Modifier
                    .height(36.dp)
                    .background(NibblerColors.SurfaceContainer, RoundedCornerShape(10.dp))
                    .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(10.dp))
                    .padding(horizontal = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                PropertyIconView(PropertyIcon.SEARCH, size = 15.dp)
                Box(modifier = Modifier.weight(1f)) {
                    if (value.isEmpty()) Text("Search issues", fontSize = 13.sp, color = NibblerColors.OnSurfaceVariant)
                    inner()
                }
            }
        }
    )
}

